import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Loader2, Check, ArrowRight, PenLine, FileWarning } from "lucide-react";
import { SITE } from "@/lib/site";
import { draftRams, ratingTone, ratingLabel, RAMS_EXAMPLES } from "@/lib/demo-engine";
import { BrowserFrame, Pill, Mono, Panel } from "@/components/mockups/primitives";
import { cn } from "@/lib/utils";

const STAGES = ["Reading the job", "Checking site facts & asbestos register", "Scoring hazards & controls", "Writing the method statement", "Applying your template"];
const fade = { initial: { opacity: 0, y: 6 }, animate: { opacity: 1, y: 0 } };

const Progress = ({ stage }) => (
  <div className="mb-3 space-y-1" data-testid="rams-demo-stages">
    {STAGES.map((s, i) => (
      <div key={s} className={cn("flex items-center gap-2 text-[11px]", i < stage ? "text-mk-dim" : i === stage ? "text-mk-text" : "text-mk-muted/60")} data-testid={`rams-demo-stage-${i}`}>
        {i < stage ? <Check className="h-3 w-3 text-mk-lime" /> : i === stage ? <Loader2 className="h-3 w-3 animate-spin text-mk-lime" /> : <span className="h-3 w-3 rounded-full border border-mk-border inline-block" />}{s}
      </div>
    ))}
  </div>
);

const Output = ({ r, stage }) => {
  const done = stage >= STAGES.length;
  return (
    <Panel title={`RAMS · ${r.task.slice(0, 48)}${r.task.length > 48 ? "…" : ""}`} right={<span className="flex items-center gap-1.5"><Pill tone="dim">{r.trade.label}</Pill>{done ? <Pill tone="lime" data-testid="rams-demo-done">v1 · draft ready</Pill> : <Pill tone="amber">drafting…</Pill>}</span>}>
      {!done && <Progress stage={stage} />}
      <div className="grid lg:grid-cols-[1.35fr_1fr] gap-3 text-[11px]">
        <div className="min-w-0">
          {stage >= 1 && (
            <motion.div {...fade} className="mb-3 grid grid-cols-2 gap-1.5" data-testid="rams-demo-facts">
              {r.facts.map(([l, v]) => <div key={l} className="rounded-md border border-mk-border bg-mk-bg/60 px-2 py-1 min-w-0"><div className="text-[9px] uppercase tracking-wider text-mk-muted">{l}</div><div className="text-mk-text truncate" title={v}>{v}</div></div>)}
            </motion.div>
          )}
          {stage >= 2 && (
            <>
              <div className="text-[10px] uppercase tracking-wider text-mk-muted mb-1.5">Risk assessment · {r.hazards.length} hazards</div>
              <div className="rounded-lg border border-mk-border overflow-hidden">
                <div className="grid grid-cols-[1.3fr_1.3fr_0.45fr_0.45fr] gap-2 border-b border-mk-border bg-mk-bg/60 px-2 py-1 text-[9px] uppercase tracking-wider text-mk-muted"><span>Hazard</span><span>Controls</span><span>Initial</span><span>Residual</span></div>
                {r.hazards.map((h, i) => (
                  <motion.div key={h.key} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.12 }} className="grid grid-cols-[1.3fr_1.3fr_0.45fr_0.45fr] gap-2 px-2 py-1.5 border-b border-mk-border/60 last:border-0 items-start" data-testid={`rams-demo-hazard-${i}`}>
                    <span className="text-mk-text"><span className="block">{h.hazard}</span><span className="block text-[9px] text-mk-muted">At risk: {h.risk}</span></span>
                    <span className="text-mk-dim">{h.controls}</span>
                    <Pill tone={ratingTone(h.initial)} title={ratingLabel(h.initial)}>{h.initial}</Pill><Pill tone={ratingTone(h.residual)}>{h.residual}</Pill>
                  </motion.div>
                ))}
              </div>
              <div className="mt-1.5 text-[9px] text-mk-muted">Scores = likelihood × severity (1–25). Low ≤5 · Medium 6–11 · High ≥12.</div>
            </>
          )}
        </div>
        <div className="min-w-0">
          {stage >= 3 && (
            <motion.div {...fade} data-testid="rams-demo-method">
              <div className="text-[10px] uppercase tracking-wider text-mk-muted mb-1.5">Method statement</div>
              <ol className="space-y-1.5">{r.steps.map((s, i) => <li key={i} className="flex gap-2"><Mono className="text-[10px] shrink-0">0{i + 1}</Mono><span className="text-mk-dim">{s}</span></li>)}</ol>
              <div className="mt-3 text-[10px] uppercase tracking-wider text-mk-muted mb-1">PPE</div>
              <div className="flex flex-wrap gap-1">{r.ppe.map((p) => <Pill key={p} tone="dim">{p}</Pill>)}</div>
            </motion.div>
          )}
          {done && (
            <motion.div {...fade} className="mt-3 rounded-lg border border-mk-border bg-mk-bg/60 p-2 text-[10px]" data-testid="rams-demo-signoff">
              <div className="flex items-center gap-1.5 text-mk-dim"><Sparkles className="h-3 w-3 text-mk-lime shrink-0" />Drafted in your house template · awaiting competent-person approval</div>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                <div className="rounded-md border border-dashed border-mk-strong p-2 h-10 flex items-center justify-between text-mk-muted"><span>Approver</span><PenLine className="h-3 w-3" /></div>
                <div className="rounded-md border border-dashed border-mk-strong p-2 h-10 flex items-center justify-between text-mk-muted"><span>Operative (on site)</span><PenLine className="h-3 w-3" /></div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </Panel>
  );
};

export default function RAMSDemo() {
  const [text, setText] = useState(RAMS_EXAMPLES[0].text);
  const [result, setResult] = useState(null);
  const [stage, setStage] = useState(-1);
  useEffect(() => {
    if (stage < 0 || stage >= STAGES.length) return undefined;
    const t = setTimeout(() => setStage((s) => s + 1), stage === 0 ? 450 : 650);
    return () => clearTimeout(t);
  }, [stage]);
  const run = () => { if (!text.trim()) return; setResult(draftRams(text)); setStage(0); };
  const busy = stage >= 0 && stage < STAGES.length;

  return (
    <div className="grid lg:grid-cols-12 gap-8 items-start" data-testid="rams-demo">
      <div className="lg:col-span-5 min-w-0">
        <label className="text-xs font-semibold uppercase tracking-wider text-mk-text" htmlFor="rams-demo-input">Describe the job</label>
        <textarea id="rams-demo-input" value={text} onChange={(e) => setText(e.target.value)} rows={7} maxLength={600} placeholder="e.g. Replace the leaking PRV on the boiler in the basement plant room at 22 Kingsway. 1970s building, occupied offices above, out of hours." className="input-dark mt-2 resize-y leading-relaxed" data-testid="rams-demo-input" />
        <div className="mt-2 flex flex-wrap gap-1.5">
          {RAMS_EXAMPLES.map((ex, i) => <button key={ex.label} type="button" onClick={() => { setText(ex.text); setStage(-1); setResult(null); }} className="rounded-full border border-mk-border bg-mk-surface px-3 py-1 text-xs text-mk-dim hover:text-mk-text hover:border-mk-lime/50 transition-colors" data-testid={`rams-demo-example-${i}`}>{ex.label}</button>)}
        </div>
        <button type="button" onClick={run} disabled={busy || !text.trim()} className="btn-lime mt-4" data-testid="rams-demo-run">{busy ? <><Loader2 className="h-4 w-4 animate-spin" />Drafting…</> : <><FileWarning className="h-4 w-4" />{result ? "Draft again" : "Draft the RAMS"}</>}</button>
        <p className="mt-4 text-xs text-mk-muted leading-relaxed">Simulation — it adapts to the trade, hazards and site clues in your text. In Fixlr the draft is built from your real job, your approved template and controls library, and the property's asbestos register, access notes and history.</p>
        {stage >= STAGES.length && (
          <motion.div {...fade} className="mt-5 flex flex-wrap gap-3" data-testid="rams-demo-cta">
            <a href={SITE.signupUrl} target="_blank" rel="noreferrer" className="btn-lime !py-2 !px-4 text-sm">Draft one on a real job <ArrowRight className="h-4 w-4" /></a>
            <Link to="/contact" className="btn-ghost !py-2 !px-4 text-sm">Book a demo</Link>
          </motion.div>
        )}
      </div>
      <div className="lg:col-span-7 min-w-0">
        <BrowserFrame url="app.fixlr.app/jobs/new/rams" className="glow-lime">
          {result ? <Output r={result} stage={stage} /> : (
            <div className="py-12 text-center" data-testid="rams-demo-empty">
              <FileWarning className="mx-auto h-8 w-8 text-mk-muted" />
              <div className="mt-3 text-sm text-mk-dim">Describe a job on the left and press <span className="text-mk-text">Draft the RAMS</span>.</div>
              <div className="mt-1 text-xs text-mk-muted">Hazards, controls, risk scores, method steps and PPE appear here.</div>
            </div>
          )}
        </BrowserFrame>
      </div>
    </div>
  );
}
